import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Mail, RefreshCw, Loader2, ArrowDownLeft, ArrowUpRight, Paperclip, ExternalLink, History } from 'lucide-react';
import { MailPermissionHint } from './DealContactsCard';
import { CollapsibleSection } from './CollapsibleSection';

function EmailViewDialog({ activityId, open, onOpenChange }) {
  const [body, setBody] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !activityId) return;
    setBody(null); setError('');
    api.getEmailBody(activityId)
      .then(r => setBody(r.data))
      .catch(err => setError(err.response?.data?.detail || 'E-Mail konnte nicht geladen werden'));
  }, [open, activityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] flex flex-col" data-testid="email-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground pr-6 truncate">
            {body?.subject || 'Email'}
          </DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-red-400 py-4" data-testid="email-view-error">{error}</p>
        ) : !body ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-2">
            <div className="text-[11px] text-muted-foreground space-y-0.5 border-b border-border pb-2">
              <p><span className="text-foreground/40">From:</span> {body.from?.name || ''} &lt;{body.from?.address || ''}&gt;</p>
              <p className="truncate"><span className="text-foreground/40">To:</span> {(body.to || []).map(t => t.address).join(', ')}</p>
              {(body.cc || []).length > 0 && (
                <p className="truncate"><span className="text-foreground/40">Cc:</span> {body.cc.map(t => t.address).join(', ')}</p>
              )}
              <p><span className="text-foreground/40">Date:</span> {(body.ts || '').replace('T', ' ').slice(0, 16)}
                <span className="ml-3 text-foreground/40">Mailbox:</span> {body.mailbox_account}</p>
            </div>
            {body.content_type === 'html' ? (
              <iframe sandbox="" srcDoc={body.body} title="email-body"
                className="flex-1 w-full min-h-[45vh] bg-white rounded-sm border border-border" data-testid="email-body-frame" />
            ) : (
              <pre className="flex-1 overflow-y-auto text-xs text-foreground whitespace-pre-wrap p-3 bg-secondary/30 rounded-sm min-h-[30vh]" data-testid="email-body-text">
                {body.body}
              </pre>
            )}
            {body.web_link && (
              <a href={body.web_link} target="_blank" rel="noreferrer"
                className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 w-fit" data-testid="email-outlook-link">
                <ExternalLink className="w-3 h-3" /> Open in Outlook
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EmailCorrespondenceCard({ projectId }) {
  const [emails, setEmails] = useState([]);
  const [lastSynced, setLastSynced] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [noPermissionBoxes, setNoPermissionBoxes] = useState([]);
  const [viewId, setViewId] = useState(null);

  const loadEmails = useCallback(async () => {
    try {
      const r = await api.getDealEmails(projectId);
      setEmails(r.data.emails || []);
      setLastSynced(r.data.last_synced_at || '');
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  const sync = useCallback(async (manual = false, full = false) => {
    setSyncing(true);
    try {
      const r = await api.syncDealEmails(projectId, full);
      setNoPermissionBoxes(r.data.no_permission || []);
      if (manual) {
        if (r.data.skipped === 'no_contacts') toast.info('Add a contact first — emails sync per linked contact');
        else toast.success(`${full ? 'Full sync (2y)' : 'Synced'} — ${r.data.synced} new email${r.data.synced !== 1 ? 's' : ''} from ${r.data.mailboxes} mailbox${r.data.mailboxes !== 1 ? 'es' : ''}`);
      }
      await loadEmails();
    } catch (err) {
      if (manual) toast.error(err.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  }, [projectId, loadEmails]);

  useEffect(() => {
    loadEmails();
    sync(false); // auto-sync on deal page open
  }, [loadEmails, sync]);

  const actions = (
    <div className="flex items-center gap-2">
      {lastSynced && (
        <span className="text-[10px] text-muted-foreground font-mono hidden sm:inline">
          synced {lastSynced.replace('T', ' ').slice(5, 16)}
        </span>
      )}
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); sync(true); }} disabled={syncing}
        data-testid="email-sync-btn"
        className="border-blue-600/40 text-blue-400 hover:bg-blue-600/10 rounded-sm text-[11px] h-7 px-2.5">
        <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Refresh
      </Button>
      <Button variant="outline" size="sm" disabled={syncing}
        onClick={(e) => { e.stopPropagation(); if (window.confirm('Full sync: re-scan the last 2 years across all connected mailboxes? This can take a moment.')) sync(true, true); }}
        data-testid="email-fullsync-btn" title="Full sync — last 2 years"
        className="border-border text-muted-foreground hover:bg-secondary/60 rounded-sm text-[11px] h-7 px-2.5">
        <History className="w-3 h-3 mr-1" /> 2y
      </Button>
    </div>
  );

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.emails`}
      icon={Mail}
      iconClass="text-blue-400"
      title="Email Correspondence"
      count={emails.length}
      right={actions}
      testId="email-correspondence-card"
      bodyClass="p-4"
    >
      {noPermissionBoxes.length > 0 && (
        <div className="mb-3">
          <MailPermissionHint />
          <p className="text-[10px] text-muted-foreground mt-1">
            Mailbox(es) without email access: {noPermissionBoxes.join(', ')}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : emails.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No emails yet — once the deal has contacts, correspondence from all connected team mailboxes appears here.
        </p>
      ) : (
        <div className="space-y-1 max-h-96 overflow-y-auto pr-1" data-testid="email-timeline">
          {emails.map(e => (
            <button key={e.id} onClick={() => setViewId(e.id)} data-testid={`email-row-${e.id}`}
              className="w-full text-left px-2.5 py-2 rounded-sm hover:bg-secondary/50 transition-colors border border-transparent hover:border-border">
              <div className="flex items-center gap-2">
                {e.direction === 'inbound'
                  ? <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  : <ArrowUpRight className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                <span className="text-xs font-medium text-foreground truncate flex-1">{e.subject || '(no subject)'}</span>
                {e.has_attachment && <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />}
                <span className="text-[10px] text-muted-foreground font-mono shrink-0">{(e.ts || '').slice(0, 10)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground truncate pl-5.5 ml-5">
                <span className={e.direction === 'inbound' ? 'text-emerald-400/80' : 'text-blue-400/80'}>
                  {e.direction === 'inbound' ? 'from' : 'to'} {e.contact_name}
                </span>
                {e.snippet ? ` — ${e.snippet}` : ''}
              </p>
            </button>
          ))}
        </div>
      )}

      <EmailViewDialog activityId={viewId} open={!!viewId} onOpenChange={(o) => !o && setViewId(null)} />
    </CollapsibleSection>
  );
}
